import {
  AsYouType,
  Examples,
  getExampleNumber,
  parsePhoneNumber,
} from "libphonenumber-js/max";
import { useEffect, useRef, useState } from "react";

import { ChevronDown } from "lucide-react";
import { CountryCode } from "./types";
import { checkPhoneExists } from "@punchup/utils/checkPhoneExists";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import examples from "libphonenumber-js/mobile/examples";
import { getDefaultCountry } from "./utils";
import { supportedCountries } from "./countries";

interface PhoneInputProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  variant?: "minimal" | "default";
  onPhoneStatusChange?: (isNewUser: boolean, showTermsAccept: boolean) => void;
  onCountryChange?: (country: CountryCode) => void;
}

const PhoneInput = ({
  phoneNumber,
  setPhoneNumber,
  variant = "default",
  onPhoneStatusChange,
  onCountryChange,
}: PhoneInputProps) => {
  const [inputNumber, setInputNumber] = useState(phoneNumber);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isCountryDirty, setIsCountryDirty] = useState(false);
  const [selectedCountry, setSelectedCountry] =
    useState<CountryCode>(getDefaultCountry());
  const [openUpward, setOpenUpward] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const supabase = createClientComponentClient();

  useEffect(() => {
    onPhoneNumberChange(phoneNumber);
  }, [phoneNumber]);

  const onCountrySelect = (country: CountryCode) => {
    const isDifferentCountry = country.code !== selectedCountry.code;
    setIsCountryDirty(true);
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);
    onCountryChange?.(country);

    // Re-selecting the SAME country is a no-op — the number is unchanged, so there's
    // no need to re-run formatting / the (DB) existence check. Only a real switch
    // clears the field: the typed number is in the previous country's format, so we
    // drop it and let the user re-enter under the new dial code. (This also avoids the
    // number's own detected country snapping the picker back — the reason picking a new
    // country previously "didn't stick" while a valid number was entered.)
    if (isDifferentCountry) {
      setInputNumber("");
      setPhoneNumber("");
    }
  };

  const toggleCountryDropdown = () => {
    // Open upward when there isn't room below (e.g. the field sits low in a
    // dialog) so the country list doesn't spill past the bottom of the screen.
    if (!isCountryDropdownOpen && triggerRef.current) {
      const spaceBelow =
        window.innerHeight - triggerRef.current.getBoundingClientRect().bottom;
      setOpenUpward(spaceBelow < 280);
    }
    setIsCountryDropdownOpen((open) => !open);
  };

  const onPhoneNumberChange = async (newNumber: string) => {
    let countryCode: CountryCode | undefined = selectedCountry;
    if (newNumber.startsWith("+")) {
      countryCode = undefined;
    }

    const ayt = new AsYouType(countryCode?.code);
    ayt.input(newNumber);
    if (ayt.isValid()) {
      const country = ayt.getCountry();
      const countryCode = supportedCountries.find((c) => c.code === country);

      if (countryCode) {
        setSelectedCountry(countryCode);
        onCountryChange?.(countryCode);
      }

      const parsedNumber = parsePhoneNumber(newNumber, {
        defaultCountry: countryCode?.code,
      });

      setInputNumber(parsedNumber.formatNational());
      setPhoneNumber(parsedNumber.number);
    } else {
      setInputNumber(newNumber);
      setPhoneNumber(newNumber);
    }
    // Run the "does this phone already belong to a user?" existence check only
    // when a consumer actually wants it (sign-up flows pass onPhoneStatusChange).
    // Without it the DB round-trip per keystroke is pure waste — e.g. a plain
    // contact-entry field just needs the formatted E.164 value.
    try {
      if (onPhoneStatusChange && newNumber.replace(/\D/g, "").length >= 10) {
        const fullNumber = newNumber.startsWith("+")
          ? newNumber
          : `${selectedCountry.dial_code}${newNumber.replace(/\D/g, "")}`;

        const parsedForCheck = parsePhoneNumber(
          fullNumber,
          selectedCountry.code
        );

        if (parsedForCheck) {
          const strippedNumber = parsedForCheck.number.replace(/^\+/, "");
          const exists = await checkPhoneExists(supabase, strippedNumber);

          const isNewUserFlag = !exists;
          const showTerms = !exists;

          // Notify parent about the updated state
          onPhoneStatusChange?.(isNewUserFlag, showTerms);
        }
      }
    } catch (error) {
      console.error("Error checking phone existence:", error);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full">
      <label htmlFor="phone" className="sr-only">
        Phone Number
      </label>
      <div className="flex items-stretch h-full">
        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            onClick={toggleCountryDropdown}
            className={cn(
              "h-full min-w-[90px] px-2 font-sans text-[16px] leading-6 border border-secondary-blue bg-white flex items-center justify-between gap-1 flex-shrink-0",
              "rounded-l-[10px]",
              variant === "minimal" ? "h-[46px]" : "h-[50px]"
            )}
          >
            <span className="flex items-center gap-2">
              <div>{selectedCountry.flag}</div>
              <span className="text-sm">{selectedCountry.dial_code}</span>
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          {isCountryDropdownOpen && (
            <div
              className={cn(
                "absolute z-50 w-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg max-h-60",
                openUpward ? "bottom-full mb-1" : "top-full mt-1"
              )}
            >
              {supportedCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className="flex items-center w-full py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                  onClick={() => onCountrySelect(country)}
                >
                  <div className="flex-shrink-0 w-6">{country.flag}</div>
                  <div className="flex-shrink-0 w-12 font-normal text-gray-700">
                    {country.dial_code}
                  </div>
                  <div className="flex-1 truncate text-left text-gray-600 pl-1">
                    {country.name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          id="phone"
          name="phone"
          type="tel"
          placeholder={
            variant === "minimal"
              ? "Enter phone number"
              : `e.g. ${
                  getExampleNumber(
                    selectedCountry.code,
                    examples as Examples
                  )?.formatNational() ?? "(201) 555-0123"
                }`
          }
          autoComplete="tel"
          required
          aria-required="true"
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          value={inputNumber}
          className={cn(
            "w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue",
            "rounded-r-[10px] -ml-px focus:outline-none",
            variant === "minimal"
              ? "h-[46px] px-3 py-2.5"
              : "h-[50px] px-4 py-3"
          )}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
